/**
 * Host-side durable-memory extraction (ENG-6200).
 *
 * Memory "dreaming" (packages/api/src/cron/memory-dreaming.ts) used to
 * consolidate durable agent_memories from the `sessions` table. That table
 * stopped being written for the Claude Code fleet at the persistent-session
 * cutover (~2026-05-09), so dreaming has promoted nothing fleet-wide since — and
 * even when it worked it was content-blind, fed only per-session tool-id lists.
 *
 * This is the replacement: for each completed end-user conversation the control
 * plane flags as pending, reconstruct the REAL transcript from the agent's local
 * session files, extract candidate memories with a small model, and POST only
 * the candidates back (POST /host/memories/candidates). The API runs the shared
 * promote gate (dedupe / threshold / embed / dream_log).
 *
 * SECURITY / DATA-MINIMISATION (the whole point of doing this host-side):
 *   The raw transcript NEVER leaves the host — identical boundary to
 *   conversation-evaluator.ts. The control plane hands us only
 *   conversation_id + channel + channel_ref (no content); the manager reads the
 *   content from on-disk transcripts, extracts locally via the agent's OWN auth
 *   (so content only reaches the same backend the evaluator already uses), and
 *   POSTs back nothing but {name, content, type, confidence, rationale}
 *   candidates — the same low-sensitivity shape the dreaming cron produced.
 *
 * Same fire-and-forget contract as conversation-evaluator.ts: self-throttled,
 * self-bounded, errors swallowed — must never slow or break the manager poll
 * cycle. Claude Code framework only. Reuses the evaluator's pluggable backend
 * (claude-p / local) and its transcript-reconstruction helpers.
 */

import {
  EXTRACTOR_IDENTIFIER_RULE,
  findIdentifierUrls,
  identifierMemoryName,
  scrubPreservingIdentifiers,
} from '@augmented/core';
import {
  reconstructConversation,
  renderTranscript,
  readRecentTurns,
  type Turn,
  type ConversationEvalBackend,
  type ConversationEvaluatorApi,
} from './conversation-evaluator.js';
import { sessionTranscriptDir } from './daily-session.js';
import { getProjectDir } from './persistent-session.js';
import { maybeEmitRoutineCandidates } from './routine-candidate-extractor.js';

// Memories are even lower-value-per-poll than evals and a completed conversation
// doesn't change, so check less often than the evaluator's 5 min.
const MIN_CHECK_INTERVAL_MS = 10 * 60_000;
// Pad the [started_at, last_message_at] window when selecting turns so a
// slightly skewed transcript timestamp still lands inside (mirrors the evaluator).
const WINDOW_PAD_MS = 5 * 60_000;

const VALID_MEMORY_TYPES = new Set(['user', 'feedback', 'project', 'reference']);

interface PendingConversation {
  conversation_id: string;
  channel: string;
  channel_ref: string;
  started_at: string;
  last_message_at: string;
  message_count: number;
}

/**
 * The low-sensitivity candidate shape POSTed to the control plane — identical to
 * packages/api/src/lib/dream-promote.ts's CandidateMemory. The API re-validates
 * via normalizeCandidates, so this is best-effort.
 */
export interface ExtractedCandidate {
  name: string;
  content: string;
  type: 'user' | 'feedback' | 'project' | 'reference';
  confidence: number;
  rationale: string;
}

// Defense-in-depth host-side scrub. The transcript is untrusted user content,
// and a prompt-injected conversation could coax the model into echoing a secret
// into a candidate's content/rationale — which would then leave the host in the
// POST body, breaking the on-host privacy boundary. The prompt asks the model
// not to, but we redact obvious secret SHAPES before anything crosses the wire.
// This is not a full DLP pass — it targets the high-signal exfil shapes (keys,
// tokens, long hex/base64 blobs, emails). The shared promote gate stores
// whatever survives.
const SECRET_PATTERNS: RegExp[] = [
  // labelled secrets: `api_key=...`, `password: ...`, `bearer ...`
  /\b[\w-]*(?:secret|password|passwd|token|api[_-]?key|bearer)[\w-]*\s*[=:]\s*\S+/gi,
  // common credential prefixes (stripe, github, slack, augmented host keys, …)
  /\b(?:sk|pk|rk|tlk|ghp|gho|ghs|xox[baprs])[-_][A-Za-z0-9]{12,}\b/g,
  // long hex blobs (hashes / hex-encoded keys)
  /\b[A-Fa-f0-9]{32,}\b/g,
  // long base64 blobs (tokens / encoded secrets)
  /\b[A-Za-z0-9+/]{40,}={0,2}\b/g,
  // email addresses
  /\b[\w.+-]+@[\w-]+\.[\w.-]+\b/g,
];

/**
 * Redact obvious secret shapes from a candidate field before it leaves the host.
 *
 * ENG-9699: routed through `scrubPreservingIdentifiers` so the blob patterns
 * above cannot shred a URL. `SECRET_PATTERNS`' base64 class is `[A-Za-z0-9+/]`
 * — it contains `/` — so applied naively to
 * `https://drive.google.com/drive/folders/<id>` it matches
 * `com/drive/folders/<id>` as one 60-char run and returns
 * `https://drive.google.[redacted]`. That is measured, not theorised, and it
 * means this scrub has ALWAYS silently destroyed exactly the Drive and Notion
 * identifiers ENG-9699 exists to preserve — the capture fix would have shipped
 * inert without this. Prose is still scrubbed with the full pattern set, where a
 * long base64 run is a leaked key rather than a document id; a URL is scrubbed
 * as a URL, losing only credential-bearing query parameters.
 */
export function scrubSensitive(text: string): string {
  return scrubPreservingIdentifiers(text, SECRET_PATTERNS);
}

interface ExtractorState {
  lastCheckedAt: number;
  /**
   * Conversations whose identifiers have already been salvaged this process
   * (ENG-9709).
   *
   * Name-dedupe in `promoteCandidates` makes a repeat submission CORRECT but
   * not QUIET: every repeat writes a `dream_log` row with
   * `rejection_reason: 'duplicate'`, and at this loop's ~10-minute cadence a
   * stuck conversation would emit ~144 of them a day, per identifier. This
   * bounds it to one submission per conversation per manager lifetime.
   *
   * EVICTION, because an unbounded set on a long-uptime process is its own bug:
   * an entry is dropped as soon as the conversation stops coming back from
   * `pending-extraction`, which is precisely when it can no longer be
   * re-submitted. See `pruneSalvaged`.
   */
  salvaged: Set<string>;
}

const state = new Map<string, ExtractorState>();

/** Test helper — reset module state. Not for production use. */
export function _resetMemoryExtractorState(): void {
  state.clear();
}

/**
 * Build the extraction prompt. Unlike the dreaming cron (which only ever saw
 * tool-id lists), this feeds the model the REAL User/Agent turns, so it can
 * extract content-based memories ("Andy prefers 5-frame carousels", "delivered
 * the Q3 deck for Two Tractors").
 */
export function buildExtractionPrompt(channel: string, transcript: string): string {
  return `You are extracting durable, reusable memories from one completed ${channel} conversation between an AI agent and an end-user. A good memory is a stable preference, fact, correction, or recurring work context that will help the agent in FUTURE conversations — not a one-off task detail or pleasantry.

${EXTRACTOR_IDENTIFIER_RULE} The "no secrets" rule below still wins: if the identifier itself carries a credential (a token, password or signing parameter), leave it out.

Conversation transcript:
"""
${transcript}
"""

Extract 0-5 candidate memories. For each:
- name: short descriptive title (max 50 chars)
- content: the memory itself, self-contained (max 200 chars — or up to 500 when it carries an identifier that must be copied exactly). Do NOT include secrets or sensitive personal data.
- type: "user" (a person's stable preference), "feedback" (a correction to how the agent should work), "project" (durable work context), or "reference" (a pointer to an external resource)
- confidence: 0.0-1.0 — how sure you are this is worth remembering long-term
- rationale: why this should persist (max 100 chars)

If there is nothing durable worth remembering, return an empty array. Do NOT invent memories to fill the list.

Respond with ONLY a JSON array, no other text:
[{"name":"...","content":"...","type":"...","confidence":0.8,"rationale":"..."}]`;
}

/**
 * Parse the model's JSON array of candidates. Tolerant of markdown fences and
 * surrounding prose.
 *
 * Returns `null` when the output contains NO parseable JSON array (no/bad JSON,
 * prose-only garbage) — a transient model hiccup the caller must RETRY, never
 * finalise. Returns an array (possibly empty) when the model produced a valid
 * array: an empty one is a legitimate "nothing durable here" that finalises the
 * conversation. This mirrors conversation-evaluator's parseVerdict→null contract.
 */
export function parseCandidates(raw: string): ExtractedCandidate[] | null {
  const match = raw.match(/\[[\s\S]*\]/);
  if (!match) return null;
  let arr: unknown;
  try {
    arr = JSON.parse(match[0]);
  } catch {
    return null;
  }
  if (!Array.isArray(arr)) return null;
  const out: ExtractedCandidate[] = [];
  for (const entry of arr) {
    if (!entry || typeof entry !== 'object') continue;
    const c = entry as Partial<ExtractedCandidate>;
    const name = typeof c.name === 'string' ? c.name.trim() : '';
    if (!name) continue;
    if (typeof c.type !== 'string' || !VALID_MEMORY_TYPES.has(c.type)) continue;
    const confidence =
      typeof c.confidence === 'number' && Number.isFinite(c.confidence)
        ? Math.max(0, Math.min(1, c.confidence))
        : 0;
    if (confidence === 0) continue;
    out.push({
      name: name.slice(0, 80),
      content: (typeof c.content === 'string' ? c.content : '').trim().slice(0, 500),
      type: c.type as ExtractedCandidate['type'],
      confidence,
      rationale: (typeof c.rationale === 'string' ? c.rationale : '').trim().slice(0, 200),
    });
  }
  return out;
}

/**
 * Confidence stamped on a detector-found identifier.
 *
 * Deliberately fixed and above `MIN_CONFIDENCE` (0.6, `dream-promote.ts`): the
 * detection is deterministic — the URL is either in the user's message or it is
 * not — so there is no uncertainty for a score to express. A candidate that
 * failed the promotion gate would be dropped silently, which is this ticket's
 * own defect one layer down.
 */
export const FORCED_IDENTIFIER_CONFIDENCE = 0.9;

/** Hard cap on forced identifiers per conversation. */
export const MAX_FORCED_IDENTIFIERS = 3;

/** Upper bound on a forced candidate's content, mirroring `parseCandidates`. */
const MAX_FORCED_CONTENT = 500;

/**
 * Deterministic backstop: identifiers the END-USER supplied that the model did
 * not carry into a candidate (ENG-9699).
 *
 * USER TURNS ONLY, and that is the main precision control rather than a detail.
 * An agent transcript is dense with URLs it fetched, cited or generated — GitHub
 * PRs, CI runs, npm pages, docs it read — and force-saving those would flood
 * `agent_memories` with exactly the noise the "don't save one-off details" rule
 * exists to prevent, degrading `context_search` for every agent. A URL a PERSON
 * typed at the agent is a different object: it is the payload of the request,
 * and it is the only half with no redundancy anywhere else once the
 * conversation ages out.
 *
 * Forced candidates are returned to be placed FIRST in the POST body: the
 * server truncates at `MAX_MEMORY_CANDIDATES_PER_POST` with a plain `.slice()`
 * and no signal, so ordering is what keeps the irreplaceable half from being
 * the half that gets dropped.
 */
export function detectMissedIdentifiers(
  turns: Turn[],
  candidates: ExtractedCandidate[],
): ExtractedCandidate[] {
  const userTurns = turns.filter((t) => t.role === 'user');
  if (userTurns.length === 0) return [];

  const already = candidates.map((c) => c.content).join('\n');
  const out: ExtractedCandidate[] = [];
  const seen = new Set<string>();

  for (const turn of userTurns) {
    for (const url of findIdentifierUrls(turn.text)) {
      if (seen.has(url)) continue;
      seen.add(url);
      // The model already carried it through verbatim — nothing to back up.
      if (already.includes(url)) continue;

      // URL FIRST so the length clamp can only ever cut the context, never the
      // identifier. A pathologically long URL keeps its bytes and loses context.
      const context = turn.text
        .replace(url, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 140);
      const content =
        url.length >= MAX_FORCED_CONTENT
          ? url
          : `${url}${context ? ` — shared by the user: ${context}` : ''}`.slice(
              0,
              MAX_FORCED_CONTENT,
            );

      out.push({
        name: identifierMemoryName(url),
        content,
        type: 'reference',
        confidence: FORCED_IDENTIFIER_CONFIDENCE,
        rationale: 'Identifier supplied by the user; unrecoverable after a restart.',
      });
      if (out.length >= MAX_FORCED_IDENTIFIERS) return out;
    }
  }
  return out;
}

/**
 * Salvage user-supplied identifiers when the extraction model call fails
 * (ENG-9709).
 *
 * Posts to `/host/memories/identifiers`, which promotes WITHOUT finalising the
 * conversation, so the model extraction a later cycle would produce is still
 * possible. Best-effort in the strongest sense: it must never throw, never
 * change control flow, and never disturb the `onModelCallError` reporting the
 * caller does for the same failure.
 *
 * A non-ok answer of ANY kind is a no-op. There is deliberately no attempt to
 * tell "this API is too old to have the route" from "the agent is not visible
 * to this host" — `verifyHostAgentAccess` returns a uniform 404 for every
 * non-ok branch by design (ENG-5704), and the correct handling is identical
 * either way: do nothing, and let the next cycle try again.
 */
async function salvageIdentifiers(args: {
  api: ConversationEvaluatorApi;
  agentId: string;
  conversationId: string;
  turns: Turn[];
  log: (msg: string) => void;
  codeName: string;
}): Promise<number> {
  const forced = detectMissedIdentifiers(args.turns, []);
  if (forced.length === 0) return 0;

  // Same scrub the finalising path applies. The payload is identical in kind —
  // a user-supplied identifier plus a slice of the surrounding turn — so it
  // must not receive weaker sanitisation just because a different branch found
  // it. Two sanitisation treatments for one payload is the drift this
  // codebase writes guards against.
  const scrubbed = forced.map((c) => ({
    ...c,
    content: scrubSensitive(c.content),
    rationale: scrubSensitive(c.rationale),
  }));

  try {
    const res = await args.api.post<{ ok?: boolean; promoted?: number }>(
      '/host/memories/identifiers',
      {
        agent_id: args.agentId,
        conversation_id: args.conversationId,
        candidates: scrubbed,
      },
    );
    if (!res?.ok) return 0;
    // Report what PERSISTED, not what was sent (CodeRabbit). The route
    // normalises and de-duplicates before promoting, so `scrubbed.length` can
    // exceed the number of rows that actually landed — and this counter exists
    // to be measured, so a number that reads high is worse than no number.
    // A duplicate means the identifier was already saved; that is not a salvage.
    return res.promoted ?? 0;
  } catch {
    return 0;
  }
}

/**
 * Drop salvaged-set entries for conversations that are no longer pending.
 *
 * Once a conversation stops being handed out it can never be re-submitted, so
 * remembering it only grows the set. Called with the ids from the current
 * `pending-extraction` page, which is the same signal the loop already has.
 */
/**
 * Run `fn` at most once per conversation per manager lifetime.
 *
 * Marked BEFORE awaiting, so two overlapping cycles cannot both fire for the
 * same conversation. A failed attempt deliberately stays marked: the identifier
 * is not lost by that (a later cycle whose model call SUCCEEDS carries it
 * through the ordinary finalising path), and un-marking would reopen exactly
 * the duplicate-per-cycle spam this exists to bound.
 */
async function salvageOnce(
  st: ExtractorState,
  conversationId: string,
  fn: () => Promise<number>,
): Promise<number> {
  if (st.salvaged.has(conversationId)) return 0;
  st.salvaged.add(conversationId);
  return fn();
}

function pruneSalvaged(st: ExtractorState, stillPending: Set<string>): void {
  for (const id of st.salvaged) {
    if (!stillPending.has(id)) st.salvaged.delete(id);
  }
}

/**
 * For one agent: pull pending conversations, reconstruct each from local
 * transcripts, extract candidate memories, and POST them. Self-throttled and
 * bounded; all faults are swallowed (Claude Code only).
 */
export async function maybeExtractMemories(args: {
  api: ConversationEvaluatorApi;
  backend: ConversationEvalBackend;
  codeName: string;
  agentId: string;
  log: (msg: string) => void;
  now?: Date;
  /** Test seam: override the transcript directory. */
  transcriptDir?: string;
  /**
   * ENG-7363: invoked when an extraction model-call (backend.run) throws, so
   * the manager can classify and report the model-API failure. Best-effort.
   */
  onModelCallError?: (err: unknown) => void;
}): Promise<void> {
  const { api, backend, codeName, agentId, log } = args;
  const now = args.now ?? new Date();
  const nowMs = now.getTime();

  const existing = state.get(codeName);
  if (existing && nowMs - existing.lastCheckedAt < MIN_CHECK_INTERVAL_MS) {
    return;
  }
  // Carry the salvaged set forward — it is per-process dedupe state, not
  // per-cycle. Re-creating it here would defeat its whole purpose.
  const st: ExtractorState = { lastCheckedAt: nowMs, salvaged: existing?.salvaged ?? new Set() };
  state.set(codeName, st);

  let pending: PendingConversation[];
  try {
    const resp = await api.get<{ ok?: boolean; conversations?: PendingConversation[] }>(
      `/host/memories/pending-extraction?agent_id=${encodeURIComponent(agentId)}`,
    );
    pending = resp?.conversations ?? [];
  } catch (err) {
    log(`[memory-extract] ${codeName}: pending fetch failed: ${(err as Error).message}`);
    return;
  }
  // Evict salvaged-set entries for conversations that have stopped coming back:
  // once a row leaves pending-extraction it can never be re-submitted, so
  // remembering it would only grow the set for the life of the process.
  //
  // Runs BEFORE the early returns below (CodeRabbit) — an empty page is exactly
  // the case where every entry is stale, so returning first would strand the
  // whole set until some later cycle happened to come back non-empty.
  pruneSalvaged(st, new Set(pending.map((p) => p.conversation_id)));

  if (pending.length === 0) return;

  // Read recent transcripts once, then mine each conversation against them.
  const dir = args.transcriptDir ?? sessionTranscriptDir(getProjectDir(codeName));
  const allTurns = readRecentTurns(dir, nowMs);
  if (allTurns.length === 0) {
    // No local transcript content at all — bump attempts so we don't spin.
    for (const conv of pending) {
      await reportSkip(api, agentId, conv.conversation_id, log, codeName);
    }
    return;
  }

  // Identifiers rescued from a failed model call this cycle (ENG-9709). Read
  // off the log line below: this rate is unmeasurable anywhere else, because a
  // salvaged identifier and a model-extracted one are the same row afterwards.
  let salvaged = 0;

  for (const conv of pending) {
    const windowStart = Date.parse(conv.started_at) - WINDOW_PAD_MS;
    const windowEnd = Date.parse(conv.last_message_at) + WINDOW_PAD_MS;
    // ENG-7137: reconstructConversation now derives both the user-match and
    // reply-routing tokens from the channel_ref itself (and credits routed
    // agent replies by id), so memory extraction gets the same higher-fidelity
    // transcript the conversation evaluator does.
    const turns = reconstructConversation(allTurns, conv.channel_ref, windowStart, windowEnd);
    if (turns.length === 0) {
      await reportSkip(api, agentId, conv.conversation_id, log, codeName);
      continue;
    }

    const transcript = renderTranscript(turns);
    if (!transcript.trim()) {
      await reportSkip(api, agentId, conv.conversation_id, log, codeName);
      continue;
    }

    // Extraction failures are transient (endpoint down, model hiccup, garbled
    // output): log and retry on a later cycle WITHOUT reportSkip — the attempts
    // budget is for permanently-unreconstructable conversations, and a
    // reconstructable one mustn't be permanently suppressed by a few hiccups.
    // A `null` parse (no valid JSON array) is the same transient class and must
    // NOT be POSTed — an empty POST would finalise the conversation and suppress
    // retries. Only a genuine array (possibly empty) is reported.
    let candidates: ExtractedCandidate[] | null;
    try {
      const out = await backend.run(buildExtractionPrompt(conv.channel, transcript));
      candidates = parseCandidates(out);
    } catch (err) {
      log(`[memory-extract] ${codeName}: extraction failed: ${(err as Error).message}`);
      // ENG-7363: surface the raw error so the manager can classify + report a
      // model-API failure for fleet early warning.
      args.onModelCallError?.(err);
      // ENG-9709: the model is what failed, not the transcript — the user's
      // identifier is sitting right there in `turns`. Salvage it now, on a path
      // that does not finalise, so a later cycle can still extract properly.
      // Reported AFTER onModelCallError so a fault here can never mask the
      // fleet-alerting signal.
      salvaged += await salvageOnce(st, conv.conversation_id, () =>
        salvageIdentifiers({ api, agentId, conversationId: conv.conversation_id, turns, log, codeName }),
      );
      continue;
    }
    if (candidates === null) {
      log(`[memory-extract] ${codeName}: unparseable output for ${conv.conversation_id.slice(0, 8)} — will retry`);
      // ENG-9709: same reasoning as the throw above. The model produced
      // something unusable, but the identifier is deterministic — it does not
      // depend on the model having been coherent.
      salvaged += await salvageOnce(st, conv.conversation_id, () =>
        salvageIdentifiers({ api, agentId, conversationId: conv.conversation_id, turns, log, codeName }),
      );
      continue;
    }

    // ENG-9699: deterministic backstop for identifiers the model summarised
    // instead of copying. Placed FIRST — the server truncates the array at
    // MAX_MEMORY_CANDIDATES_PER_POST with a bare `.slice()` and no signal, and
    // the identifier is the one part of the payload nobody can re-derive.
    //
    // ENG-9709 closed the ordinary case this comment used to describe: a model
    // failure now salvages the identifier immediately via
    // `/host/memories/identifiers`, which promotes WITHOUT finalising, so a
    // later cycle can still extract properly.
    //
    // WHAT REMAINS OPEN, stated exactly, because it is narrower than it looks
    // and a vague bound is worse than none. Salvage happens on the model-call
    // failure, which is reached only AFTER the transcript reconstructed — so it
    // fires on the FIRST poll of an outage, while the transcript is minutes
    // old. It cannot fire for a conversation that is never polled while fresh:
    // one starved past the `pending-extraction` fetch ceiling, or one whose
    // manager was down beyond the 7-day `TRANSCRIPT_MTIME_WINDOW_MS`. Those
    // reach `turns.length === 0` and are skipped ABOVE this point — and that
    // branch DOES call `reportSkip`, which bumps `memory_extract_attempts`, so
    // after three the conversation leaves pending-extraction permanently with
    // `memory_extracted_at` still null. That residual is not fixed here.
    const forced = detectMissedIdentifiers(turns, candidates);
    const merged = [...forced, ...candidates];

    // Host-side scrub: redact obvious secret shapes the model may have echoed
    // from an injected transcript before the candidates leave the box.
    const scrubbed = merged.map((c) => ({
      ...c,
      content: scrubSensitive(c.content),
      rationale: scrubSensitive(c.rationale),
    }));

    try {
      const res = await api.post<{ ok?: boolean; promoted?: number; logged?: number }>(
        '/host/memories/candidates',
        {
          agent_id: agentId,
          conversation_id: conv.conversation_id,
          candidates: scrubbed,
        },
      );
      log(
        `[memory-extract] ${codeName}: ${conv.conversation_id.slice(0, 8)} → ${candidates.length} candidate(s), ${forced.length} identifier(s) recovered, ${res?.promoted ?? 0} promoted`,
      );
    } catch (err) {
      log(`[memory-extract] ${codeName}: report failed: ${(err as Error).message}`);
    }

    // ADR-0041 D4 (ENG-7420): same pass, second product - look for a durable
    // recurring ask and emit a routine candidate. Rides this loop's throttle
    // and reconstruction; input is the USER turns only (injection boundary
    // lives in routine-candidate-extractor.ts); never throws. Runs after the
    // memory report because that POST finalises the conversation server-side -
    // this is the one emission opportunity for this conversation.
    await maybeEmitRoutineCandidates({
      api,
      backend,
      codeName,
      agentId,
      conversationId: conv.conversation_id,
      channel: conv.channel,
      turns,
      log,
      onModelCallError: args.onModelCallError,
    });
  }

  // ENG-9709: salvage rate. Logged here rather than server-side because it is
  // unmeasurable there — once promoted, an identifier rescued from a failed
  // model call is indistinguishable from one the model extracted normally.
  if (salvaged > 0) {
    log(`[memory-extract] ${codeName}: identifiers_salvaged=${salvaged} (model call failed)`);
  }
}

/** Report a conversation we couldn't reconstruct, bumping its retry counter. */
async function reportSkip(
  api: ConversationEvaluatorApi,
  agentId: string,
  conversationId: string,
  log: (msg: string) => void,
  codeName: string,
): Promise<void> {
  try {
    await api.post('/host/memories/candidates', {
      agent_id: agentId,
      conversation_id: conversationId,
      skipped: true,
    });
  } catch (err) {
    log(`[memory-extract] ${codeName}: skip report failed: ${(err as Error).message}`);
  }
}
